/* eslint @typescript-eslint/camelcase: ["error", { "properties": "never" }] */
/**
 * V0.2 数据源层：接口 / mock 双源（apiConfig.USE_API 切换）
 * API 参数/响应字段沿用服务端 snake_case，TypeScript 变量继续使用 camelCase。
 * 接口 = secret-list-php /lt/live-trailer-v2/*（响应 {result:'success', data}）
 * mock  = ./mock/data.ts（联调前静态演示）
 */
import axios from 'axios';
import { USE_API, API_BASE, currentGhId } from './apiConfig';
import type { Anchor, Offer, ProductQuery, Session, SessionProduct } from './mock/types';
import {
  ALL_GOODS,
  ANCHORS,
  anchorOf,
  CATEGORIES,
  findProduct,
  goodsOf,
  previewOf,
  SESSIONS,
} from './mock/data';

/* ========= 通用 ========= */
const STATUS_NAME: Record<number, Session['status']> = { 0: 'pending', 1: 'living', 2: 'ended' };

type ApiResp<T> = { data?: T; err?: unknown };
export type CategoryOption = { id: string; label: string; count: number };

/** S07：请求失败统一走 { err }，由页面转 T02/T11；G07：10s 超时；同 uri 竞态取消 */
const API_TIMEOUT = 10000;

async function apiGet<T>(uri: string, params: Record<string, string | number | undefined>): Promise<ApiResp<T>> {
  // gh_id 渠道透传：随 query 下发（自定义请求头会触发 CORS 预检，浏览器端只用 query）
  const gh = currentGhId();
  const finalParams = gh ? { gh_id: gh, ...params } : params;
  // S07：同一接口的新请求取消旧请求；仅最新响应可更新界面
  const prev = (window as any).v2Cancel?.[uri];
  if (prev) prev();
  const source = axios.CancelToken.source();
  ((window as any).v2Cancel ??= {})[uri] = source.cancel;

  try {
    const { data } = await axios.get(`${API_BASE}/${uri}`, {
      params: finalParams,
      timeout: API_TIMEOUT,
      cancelToken: source.token,
    });
    if (data.result === 'success') return { data: data.data };
    return { err: data.message ?? '请求失败' };
  } catch (e: any) {
    if (axios.isCancel(e)) return { err: 'cancelled' }; // 过期响应不更新界面（S07）
    return { err: e?.message ?? 'network error' }; // T11/T02 由页面处理
  }
}

function labelOf(categoryId: string): string {
  return CATEGORIES.find((c) => c.id === categoryId)?.label ?? '';
}

/** 场次元数据缓存（schedule 拉回后填充；搜索/精选/详情组装 Session 用） */
export const sessionCache: Record<string, Session> = {};
export function cacheSessions(sessions: Session[]) {
  sessions.forEach((s) => { sessionCache[s.id] = s; });
}
/** scene（liveIdSceneMap 值）→ 主播 live_id */
const SCENE_ANCHOR_ID: Record<string, number> = { lijiaqi: 1, girls: 2, girls_wardrobe: 3 };

/** mock 模式：scene 标识（scene key / 主播名 / id 串）反查 anchorId */
function anchorIdByScene(scene?: string): number | undefined {
  if (!scene) return undefined;
  if (SCENE_ANCHOR_ID[scene]) return SCENE_ANCHOR_ID[scene];
  const a = ANCHORS.find((x) => x.name === scene || `${x.id}` === scene);
  return a?.id;
}


/* ========= 1. 主播列表 ========= */
export async function fetchAnchors(): Promise<Anchor[]> {
  if (!USE_API) return ANCHORS;
  const { data, err } = await apiGet<{ list: any[] }>('anchors', {});
  if (err) throw new Error(String(err));
  return (data?.list ?? []).filter((a) => !a.hidden).map((a) => ({
    id: a.live_id,
    name: a.name,
    avatar: a.avatar || '',
    tags: a.tags || '',
    listTemplate: (a.template as 'main' | 'compact') || 'compact',
    liveUrl: a.live_url || '',
    scene: a.scene || '',
  }));
}

/* ========= 2. 日期排期 ========= */
export async function fetchSchedule(dateStart: string, dateEnd: string, includeGoods = true): Promise<Session[]> {
  if (!USE_API) {
    // mock：附 C02 预览（前 3 个商品）
    return SESSIONS.filter((s) => s.date >= dateStart && s.date <= dateEnd).map((s) => ({
      ...s,
      previews: includeGoods ? previewOf(s.id).map((g) => ({
        image: g.image,
        linkNo: g.linkNo,
        price: g.offers[0]?.price ?? null,
      })) : [],
    }));
  }
  const { data, err } = await apiGet<{ list: any[] }>('schedule', {
    date_start: dateStart, date_end: dateEnd,
    ...(includeGoods ? {} : { include_goods: 0 }),
  });
  if (err) throw new Error(String(err));
  const list = (data?.list ?? []).map((r) => ({
    id: `a-${r.activity_id}`,
    anchorId: r.live_id,
    date: r.date,
    startTime: r.start_time,
    endTime: r.end_time,
    status: STATUS_NAME[r.status] ?? 'pending',
    theme: r.theme || '',
    themeIco: r.theme_ico || '',
    updatedAt: '',
    previews: (r.previews ?? []).map((p: any) => ({ image: p.image, linkNo: p.link_no, price: p.price })),
  })) as Session[];
  cacheSessions(list);
  return list;
}

/** 场次选项（L04 选择框）：tb 有商品的场次反查，单房间单日期 */
export async function fetchSessionOptions(scene: string, date: string): Promise<Session[]> {
  if (!USE_API) {
    const aid = SCENE_ANCHOR_ID[scene] ?? 0;
    return SESSIONS.filter((s) => s.anchorId === aid && s.date === date).map((s) => ({
      ...s,
      previews: previewOf(s.id).map((g) => ({ image: g.image, linkNo: g.linkNo, price: g.offers[0]?.price ?? null })),
    }));
  }
  const { data, err } = await apiGet<{ list: any[] }>('session-options', { scene, activity_date: date });
  if (err) throw new Error(String(err));
  const list = (data?.list ?? []).map((r) => ({
    id: `a-${r.activity_id}`,
    anchorId: SCENE_ANCHOR_ID[scene] ?? 0,
    date,
    startTime: r.start_time,
    endTime: r.end_time,
    status: STATUS_NAME[r.status] ?? 'pending',
    theme: r.theme || '',
    themeIco: '',
    updatedAt: '',
    previews: [],
  })) as Session[];
  cacheSessions(list);
  return list;
}

/* ========= 3. 场次分类与商品 ========= */

/** 场次分类 chips（主页）：category_id2 → xhs_category.label；全部 chip 由页面固定加。
 * 返回 null = 接口不可用，页面保留默认四分类 chips */
export async function fetchSessionCategories(scene: string, date: string, activityId?: number): Promise<{ id: string; label: string; count: number }[] | null> {
  if (!USE_API) {
    // mock：沿用四分类样例（仅演示态使用）
    return CATEGORIES.filter((c) => c.id !== 'all').map((c) => ({ id: c.id, label: c.label, count: 0 }));
  }
  const ret = await apiGet<{ list: any[] }>('session-categories', {
    scene,
    activity_date: date,
    activity_id: activityId,
  });
  if (!ret.data) return null;
  return (ret.data.list ?? []).map((r) => ({ id: String(r.category_id2), label: String(r.label ?? ''), count: Number(r.goods_count ?? 0) }));
}

function mapOffers(list: any[]): Offer[] {
  return (list ?? []).map((o) => ({
    specId: String(o.specId),
    name: String(o.name ?? ''),
    planGroup: o.planGroup || null,
    buyCount: Number(o.buyCount ?? 1),
    toHandQty: Number(o.toHandQty ?? 1),
    toHandUnit: String(o.toHandUnit ?? '件'),
    price: o.price === null || o.price === undefined || o.price === '' ? null : String(o.price),
    condition: String(o.condition ?? ''),
    gift: {
      has: !!(o.gift && o.gift.has),
      desc: o.gift?.desc ?? null,
      images: (o.gift?.images ?? []).filter(Boolean),
    },
  }));
}

const CATEGORY_ID_BY_LABEL: Record<string, string> = CATEGORIES.reduce((acc, c) => {
  acc[c.label] = c.id;
  return acc;
}, {} as Record<string, string>);

function apiRowToProduct(row: any, session: Session): SessionProduct {
  return {
    sessionProductId: String(row.session_product_id),
    session,
    linkNo: Number(row.link_no ?? 0),
    itemId: String(row.item_id ?? ''),
    itemName: String(row.item_name ?? ''),
    brand: String(row.brand ?? ''),
    categoryId: row.category_label ? CATEGORY_ID_BY_LABEL[String(row.category_label)] ?? 'all' : 'all',
    image: String(row.image ?? ''),
    offers: mapOffers(row.offers),
    buyUrl: row.short_url ? String(row.short_url) : row.url ? String(row.url) : '',
    buyScheme: String(row.url_scheme ?? ''),
    taotoken: String(row.taotoken ?? ''),
    purchaseRequiresRefresh: row.purchase_requires_refresh === true,
  };
}

/** 搜索/精选/详情行组装 Session：优先行内场次信息，schedule 缓存补缺 */
function sessionFromRow(r: any): Session {
  const hasSource = Number(r.activity_id) > 0;
  const cached = hasSource ? sessionCache[`a-${r.activity_id}`] : undefined;
  const hasOriginMetadata = r.origin_scene !== undefined || r.live_id !== undefined;
  const anchorId = hasOriginMetadata
    ? Number(r.live_id) || SCENE_ANCHOR_ID[r.origin_scene] || 0
    : SCENE_ANCHOR_ID[r.scene] ?? cached?.anchorId ?? 0;
  return {
    id: hasSource ? `a-${r.activity_id}` : '',
    anchorId: hasSource ? anchorId : 0,
    date: r.activity_date ?? cached?.date ?? '',
    startTime: r.start_time ?? cached?.startTime ?? '',
    endTime: r.end_time ?? cached?.endTime ?? '',
    status: STATUS_NAME[r.session_status] ?? cached?.status ?? 'pending',
    theme: r.theme ?? cached?.theme ?? '',
    themeIco: cached?.themeIco || '',
    updatedAt: '',
    previews: cached?.previews ?? [],
  };
}

export interface GoodsQuery extends ProductQuery {
  scene: string;
  activityDate: string;
  anchorId?: number; // mock 模式按主播过滤场次
  sessionId?: string; // mock 模式精确场次（同日多场）
  activityId?: number;
  page: number;
  pageSize?: number;
  sortType?: 'default' | 'price_asc' | 'price_desc';
  priceStatus?: 'all' | 'published' | 'pending';
  minPrice?: string;
  maxPrice?: string;
}

type PriceQuery = Pick<GoodsQuery, 'sortType' | 'priceStatus' | 'minPrice' | 'maxPrice'>;
function priceParams(q: PriceQuery) {
  return { sort_type: q.sortType, price_status: q.priceStatus, min_price: q.minPrice, max_price: q.maxPrice };
}

/** 场次商品：返回 {list, total, hasMore, updateTime} */
export async function fetchSessionGoods(q: GoodsQuery): Promise<{ list: SessionProduct[]; total: number; hasMore: boolean; updateTime: string }> {
  if (!USE_API) {
    // mock：优先按精确场次（同日多场 L04），否则按主播+日期
    const sess = q.sessionId
      ? SESSIONS.find((s) => s.id === q.sessionId)
      : SESSIONS.find((s) => s.date === q.activityDate && (q.anchorId ? s.anchorId === q.anchorId : true));

    const list = sess ? goodsOf(sess.id) : [];

    return { list, total: list.length, hasMore: false, updateTime: sess?.updatedAt ?? '10:30' };
  }
  const numericCategory = !!q.categoryId && /^\d+$/.test(q.categoryId);
  const { data, err } = await apiGet<any>('session-goods', {
    scene: q.scene,
    activity_date: q.activityDate,
    activity_id: q.activityId,
    page: q.page,
    page_size: q.pageSize,
    category_id2: numericCategory ? q.categoryId : '',
    // 元数据接口失败时页面沿用四大分类，按标签筛选，不能将 home/beauty 当作数字 ID。
    category_label: !numericCategory && q.categoryId && q.categoryId !== 'all' ? labelOf(q.categoryId) : '',
    ...priceParams(q),
  });
  if (err) throw new Error(String(err));
  return {
    list: (data?.list ?? []).map((r: any) => {
      const session = sessionFromRow(r);
      session.updatedAt = data?.update_time ?? '';
      return apiRowToProduct(r, session);
    }),
    total: data?.total ?? 0,
    hasMore: !!data?.has_more,
    updateTime: data?.update_time ?? '',
  };
}

/* ========= 4. 范围搜索 ========= */
export async function searchGoods(params: PriceQuery & {
  scope: 'all' | 'anchor';
  scene?: string;
  keyword: string;
  dateRange?: 'today7' | 'today' | 'all';
  page?: number;
  categoryId?: string;
}): Promise<{ list: SessionProduct[]; total: number; hasMore: boolean }> {
  if (!USE_API) {
    const k = params.keyword.trim().toLowerCase();
    if (!k) return { list: [], total: 0, hasMore: false };
    const pool = params.scope === 'all' ? ALL_GOODS : ALL_GOODS.filter((g) => g.session.anchorId === (anchorIdByScene(params.scene) ?? 0));
    const list = pool.filter((g) => {
      const anchor = anchorOf(g.session.anchorId);
      return [g.itemName, g.brand, anchor.name, String(g.linkNo)].some((f) => f.toLowerCase().includes(k) || String(g.linkNo) === k);
    });
    return { list, total: list.length, hasMore: false };
  }
  const { data, err } = await apiGet<any>('search', {
    scope: params.scope,
    scene: params.scope === 'anchor' ? params.scene : '',
    keyword: params.keyword,
    date_range: params.dateRange ?? 'today7',
    page: params.page ?? 1,
    category_label: params.categoryId && params.categoryId !== 'all' ? labelOf(params.categoryId) : '',
    ...priceParams(params),
  });
  if (err) throw new Error(String(err));
  const list = (data?.list ?? []).map((r: any) => apiRowToProduct(r, sessionFromRow(r)));
  return { list, total: data?.total ?? 0, hasMore: !!data?.has_more };
}

/* ========= 5. 精选推荐 ========= */
export async function fetchRecommend(params: PriceQuery & { page?: number; categoryId?: string }): Promise<{ list: SessionProduct[]; total: number; hasMore: boolean; categories: CategoryOption[] }> {
  if (!USE_API) {
    // mock：featured 演示数据（主题分组由页面处理，此处返回 item 集合）
    let items = ALL_GOODS.filter((g) => ['sp-1', 'sp-3', 'sp-5', 'sp-7'].includes(g.sessionProductId));
    const categories = CATEGORIES.filter((c) => c.id !== 'all')
      .map((c) => ({ ...c, count: items.filter((g) => g.categoryId === c.id).length }))
      .filter((c) => c.count > 0);
    if (params.categoryId && params.categoryId !== 'all') items = items.filter((g) => g.categoryId === params.categoryId);
    if (params.sortType && params.sortType !== 'default') {
      const price = (g: SessionProduct) => Math.min(...g.offers.filter((o) => o.price !== null).map((o) => Number(o.price)));
      items.sort((a, b) => {
        const pa = price(a), pb = price(b);
        if (!Number.isFinite(pa)) return Number.isFinite(pb) ? 1 : 0;
        if (!Number.isFinite(pb)) return -1;
        return params.sortType === 'price_asc' ? pa - pb : pb - pa;
      });
    }
    return { list: items, total: items.length, hasMore: false, categories };
  }
  const numericCategory = !!params.categoryId && /^\d+$/.test(params.categoryId);
  const { data, err } = await apiGet<any>('recommend', {
    page: params.page ?? 1,
    category_id2: numericCategory ? params.categoryId : '',
    category_label: !numericCategory && params.categoryId && params.categoryId !== 'all' ? labelOf(params.categoryId) : '',
    ...priceParams(params),
  });
  if (err) throw new Error(String(err));
  const list = (data?.list ?? []).map((r: any) => apiRowToProduct(r, sessionFromRow(r)));
  const categories: CategoryOption[] = (data?.categories ?? [])
    .map((r: any) => ({ id: String(r.category_id2), label: String(r.label ?? ''), count: Number(r.goods_count ?? 0) }))
    .filter((c: CategoryOption) => c.label.trim() !== '');
  return { list, total: data?.total ?? 0, hasMore: !!data?.has_more, categories };
}

/* ========= 6. 商品详情 ========= */
export async function fetchGoodsDetail(params: { sourceId?: string; sessionProductId?: string }): Promise<SessionProduct | null> {
  if (!USE_API) return params.sessionProductId ? (findProduct(params.sessionProductId) ?? null) : null;
  const { data, err } = await apiGet<any>('goods-detail', { source_id: params.sourceId, id_live_today: params.sessionProductId });
  if (err) throw new Error(String(err));
  if (!data) return null;
  return apiRowToProduct(data, sessionFromRow(data));
}

export type PurchaseLinks = Pick<SessionProduct, 'buyUrl' | 'buyScheme' | 'taotoken'>;

/** Explicit click-time refresh; transport timeouts and all other failures have separate messages. */
export async function resolvePurchaseLink(sessionProductId: string): Promise<PurchaseLinks> {
  let timedOut = false;
  try {
    const ghId = currentGhId();
    const { data } = await axios.get(`${API_BASE}/purchase-link`, {
      params: { id_live_today: sessionProductId, ...(ghId ? { gh_id: ghId } : {}) },
      timeout: API_TIMEOUT,
    });
    if (data?.result !== 'success') {
      timedOut = Number(data?.code) === 40801;
      throw new Error('purchase failed');
    }
    const row = data.data;
    const links: PurchaseLinks = {
      buyUrl: String(row?.short_url || row?.url || '').trim(),
      buyScheme: String(row?.url_scheme || '').trim(),
      taotoken: String(row?.taotoken || '').trim(),
    };
    if (!links.buyUrl || !links.taotoken) throw new Error('incomplete purchase links');
    return links;
  } catch (error: any) {
    timedOut = timedOut || error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
      || [408, 504].includes(error?.response?.status) || Number(error?.response?.data?.code) === 40801;
    throw new Error(timedOut ? '购买链接获取失败，请稍后重试' : '商品已下架或购买链接已失效');
  }
}
